import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { 
  useGetDepartmentsQuery, 
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation
} from '../slices/departmentsApiSlice';
import { useGetFacultyQuery } from '../slices/facultyApiSlice';
import { motion } from 'framer-motion';
import { FullscreenLoader } from '../utils/LoaderUtils';

const DepartmentManagementScreen = () => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    head: '',
    isActive: true
  });

  const { data: departments, isLoading, refetch, error } = useGetDepartmentsQuery();
  const { data: faculty } = useGetFacultyQuery();
  
  // Log faculty data to check structure
  useEffect(() => {
    if (faculty) {
      console.log('Faculty data available:', faculty);
      // Log each faculty member's structure in detail
      faculty.forEach((f, index) => {
        console.log(`Faculty ${index + 1}: ${f.name}`, {
          hasUser: !!f.user,
          userId: f.user?._id,
          userEmail: f.user?.email,
          designation: f.designation,
          facultyId: f._id
        });
      });
    }
  }, [faculty]);
  
  const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation();
  const [deleteDepartment, { isLoading: isDeleting }] = useDeleteDepartmentMutation();

  useEffect(() => {
    if (faculty && departments) {
      // Log faculty HODs and compare with department heads
      const hods = faculty.filter(f => f.designation === "Head of Department (HOD)");
      console.log('Available HODs:', hods.map(h => ({ name: h.name, userId: h.user?._id })));
      
      // Log department heads for comparison
      console.log('Department heads:', departments.map(d => ({ 
        dept: d.name, 
        headId: d.head?._id,
        headName: d.head?.name
      })));
      
      // If editing, compare head IDs
      if (selectedDepartment && selectedDepartment.head) {
        console.log('Currently editing department with head ID:', selectedDepartment.head._id);
        console.log('Current formData head value:', formData.head);
        
        // Find matching HOD
        const matchingHod = hods.find(h => h.user && h.user._id === selectedDepartment.head._id);
        console.log('Matching HOD found:', matchingHod?.name || 'None');
      }
    }
  }, [faculty, departments, selectedDepartment, formData.head]);

  useEffect(() => {
    if (departments) {
      console.log('Complete departments response:', JSON.stringify(departments[0], null, 2));
    }
  }, [departments]);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      head: '',
      isActive: true
    });
    setSelectedDepartment(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCreateClick = () => {
    setModalMode('create');
    resetForm();
    setShowModal(true);
  };

  const handleEditClick = (department) => {
    console.log("Edit department:", department);
    console.log("Department head:", department.head);
    
    // Check if head is populated and extract ID
    let headId = null;
    if (department.head) {
      // Log the complete head object structure
      console.log("Head object structure:", JSON.stringify(department.head, null, 2));
      
      // If head is a user object directly
      if (department.head._id) {
        headId = department.head._id.toString();
        console.log("Head is a user/faculty object with ID:", headId);
      }
    }
    
    console.log("Extracted head ID:", headId);
    
    setFormData({
      _id: department._id,
      name: department.name,
      head: headId,
      code: department.code,
      description: department.description || '',
      isActive: department.isActive
    });
    
    // Find matching faculty member for logging purposes
    if (headId && faculty?.length) {
      // Try to match by user ID first
      let matchingFaculty = faculty.find(f => f.user && f.user._id && f.user._id.toString() === headId);
      
      // If no match by user ID, try to match by faculty ID
      if (!matchingFaculty) {
        matchingFaculty = faculty.find(f => f._id.toString() === headId);
      }
      
      console.log("Matching faculty:", matchingFaculty?.name, 
        matchingFaculty?.user?._id ? `User ID: ${matchingFaculty.user._id}` : `Faculty ID: ${matchingFaculty?._id}`);
    } else {
      console.log("No head assigned or no faculty available");
    }
    
    setModalMode('edit');
    setSelectedDepartment(department);
    setShowModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      try {
        await deleteDepartment(id).unwrap();
        toast.success('Department deleted successfully');
        refetch();
      } catch (error) {
        toast.error(error?.data?.message || 'Failed to delete department');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare submission data
    const submissionData = { ...formData };
    
    // Ensure head field is properly formatted (empty string if not set)
    if (submissionData.head === undefined || submissionData.head === null) {
      submissionData.head = '';
    }
    
    // Log the head value being submitted
    console.log('Head value being submitted:', submissionData.head, 'Type:', typeof submissionData.head);
    
    // If we have a head ID, check if it's a valid ID
    if (submissionData.head) {
      console.log("Looking up head ID:", submissionData.head);
      
      // Try to find the faculty by ID (either user ID or faculty ID)
      const matchingFaculty = faculty?.find(f => 
        (f.user && f.user._id && f.user._id.toString() === submissionData.head) || 
        (f._id.toString() === submissionData.head)
      );
      
      if (matchingFaculty) {
        console.log("Found matching faculty for head:", matchingFaculty);
        
        // Check if faculty has a user and use that ID
        if (matchingFaculty.user && matchingFaculty.user._id) {
          // API expects the user ID, not the faculty ID
          submissionData.head = matchingFaculty.user._id.toString();
          console.log("Using user ID for department head:", submissionData.head);
        } else {
          console.warn("Faculty has no user ID. This might cause issues with the backend.");
          toast.warning(`Note: Faculty "${matchingFaculty.name}" has no user account assigned. Assigning as department head may not work properly. Please assign a user account to this faculty member first.`);
          
          // Try to proceed by using faculty ID, but this might not work with the backend
          submissionData.head = ""; // Empty the head field if no user is available
        }
      } else {
        console.warn("No matching faculty found for head ID:", submissionData.head);
      }
    }
    
    try {
      let result;
      
      if (modalMode === 'create') {
        console.log('Creating department with data:', JSON.stringify(submissionData, null, 2));
        result = await createDepartment(submissionData).unwrap();
        console.log('Department created response:', JSON.stringify(result, null, 2));
        toast.success('Department created successfully');
        
        // Check if head was properly assigned
        if (result._id) {
          setTimeout(() => checkBackendStatus(result._id), 1000); 
        }
      } else {
        const updateData = {
          id: selectedDepartment._id,
          ...submissionData
        };
        console.log('Updating department with data:', JSON.stringify(updateData, null, 2));
        
        // Log the head value in detail
        if (typeof updateData.head === 'object') {
          console.log('Head is an object:', updateData.head);
        } else {
          console.log('Head is a primitive value:', updateData.head, 'of type:', typeof updateData.head);
        }
        
        result = await updateDepartment(updateData).unwrap();
        console.log('Department updated response:', JSON.stringify(result, null, 2));
        toast.success('Department updated successfully');
        
        // Check if head was properly assigned
        if (selectedDepartment?._id) {
          setTimeout(() => checkBackendStatus(selectedDepartment._id), 1000);
        }
      }
      
      handleCloseModal();
      
      // Force refresh to see changes
      setTimeout(async () => {
        const refreshedData = await refetch();
        console.log('Refetched departments:', JSON.stringify(refreshedData.data?.[0], null, 2));
        // Reload the entire page to ensure we get fresh data
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error('Error saving department:', err);
      console.error('Error details:', err?.data);
      toast.error(err?.data?.message || 'Failed to save department');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Ensure value is never null or undefined for select elements
    const finalValue = (type === 'select-one' && (value === null || value === undefined)) ? '' : value;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : finalValue
    });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { duration: 0.3 }
    }
  };

  // Function to check backend structure and report any issues
  const checkBackendStatus = async (departmentId) => {
    try {
      // Call refetch to update data
      const refreshedData = await refetch();
      console.log('Refetched department data:', refreshedData);

      // Find the specific department that was updated
      const updatedDepartment = refreshedData.data?.find(d => d._id === departmentId);
      if (updatedDepartment) {
        console.log('Updated department data:', JSON.stringify(updatedDepartment, null, 2));
        
        // Check if head is properly assigned
        if (updatedDepartment.head) {
          console.log('Head successfully assigned:', updatedDepartment.head);
        } else {
          console.error('Head not assigned in the backend. API might have rejected the head ID.');
          toast.error('Department head assignment failed. Please check the faculty user account.');
        }
      } else {
        console.warn('Could not find updated department in refetched data');
      }
    } catch (error) {
      console.error('Error checking department status:', error);
    }
  };

  if (isLoading) return <FullscreenLoader text="Loading departments..." />;

  if (error) return (
    <div className="text-red-500 text-center p-4">
      {error?.data?.message || 'Failed to load departments'}
    </div>
  );

  return (
    <motion.div 
      className={`p-6 pt-24 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'} min-h-screen`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Department Management</h1>
          <button
            onClick={handleCreateClick}
            className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <FaPlus className="mr-2" />
            Add Department
          </button>
        </div>

        {departments?.length === 0 ? (
          <div className={`text-center p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
            <p>No departments found. Create your first department to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
              <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Head</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {departments?.map((department) => (
                  <tr key={department._id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">{department.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{department.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {department.head ? (
                        <div>
                          {console.log(`Rendering department head for ${department.name}:`, department.head)}
                          <div className="font-medium">{department.head.name}</div>
                          {department.head.email && (
                            <div className="text-xs text-gray-500">{department.head.email}</div>
                          )}
                          <div className="text-xs text-blue-500">ID: {department.head._id}</div>
                        </div>
                      ) : (
                        <div>
                          <span className="text-amber-500 font-medium">Not assigned</span>
                          <div className="text-xs text-gray-500 mt-1">
                            Ensure faculty has a user account linked.
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        department.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {department.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditClick(department)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(department._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className={`inline-block align-bottom ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`}>
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`}>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium">
                        {modalMode === 'create' ? 'Create Department' : 'Edit Department'}
                      </h3>
                      <div className="mt-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium">
                              Department Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Department Code
                            </label>
                            <input
                              type="text"
                              name="code"
                              value={formData.code}
                              onChange={handleChange}
                              required
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Description
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              rows="3"
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            ></textarea>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Department Head
                            </label>
                            <select
                              name="head"
                              value={formData.head || ''}
                              onChange={(e) => {
                                console.log("Selected head value:", e.target.value);
                                handleChange(e);
                              }}
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            >
                              <option value="">None</option>
                              {faculty?.map((f) => {
                                // Only show faculty with HOD designation
                                if (f.designation !== "Head of Department (HOD)") return null;
                                
                                console.log(`Processing HOD faculty: ${f.name}`, {
                                  hasUser: !!f.user,
                                  userId: f.user?._id,
                                  facultyId: f._id
                                });
                                
                                // Use faculty ID as value if user ID is missing
                                const optionValue = f.user?._id ? f.user._id.toString() : f._id.toString();
                                const optionLabel = f.name + (f.user?.email ? ` - ${f.user.email}` : '');
                                const formHeadId = formData.head ? formData.head.toString() : '';
                                const isSelected = formHeadId === optionValue;
                                
                                console.log(`HOD option: ${optionLabel}, value: ${optionValue}, formData.head: ${formHeadId}, isSelected: ${isSelected}`);
                                
                                return (
                                  <option key={f._id} value={optionValue}>
                                    {optionLabel}
                                    {isSelected ? ' (Current)' : ''}
                                  </option>
                                );
                              }).filter(Boolean)}
                            </select>
                            {faculty?.filter(f => f.designation === "Head of Department (HOD)" && f.user && f.user._id).length === 0 && (
                              <p className="mt-1 text-xs text-amber-500">
                                No faculty with HOD designation available. Assign "Head of Department (HOD)" designation to faculty members first.
                              </p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                              Note: Only faculty with "Head of Department (HOD)" designation are displayed here.
                            </p>
                          </div>
                          
                          {modalMode === 'edit' && (
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                name="isActive"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={handleChange}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                              />
                              <label htmlFor="isActive" className="ml-2 block text-sm">
                                Active
                              </label>
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isCreating || isUpdating ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className={`mt-3 w-full inline-flex justify-center rounded-md border ${isDarkMode ? 'border-gray-600 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 bg-white hover:bg-gray-50'} shadow-sm px-4 py-2 text-base font-medium sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DepartmentManagementScreen; 